// CHAT-PANEL (founder review 2026-10-28): the Memory tab beside a conversation searches Recall
// with THAT conversation, not the company's name: the channel and topic, the open thread's root
// message and the DE it goes to (the "→ Riley" addressee). With no thread open, the channel and
// topic. Everything here is read from queries the chat screen and the thread panel already hold
// (same keys), so the context adds at most the thread's addressee call (when the thread composer
// has not asked it: a company with no agents).
import {
  skipToken,
  type UseQueryResult,
  useInfiniteQuery,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { useMemo, useRef } from "react";
import type { Focus } from "../../focus/focus";
import { channelLabel } from "./chat-model";
import {
  channelQuery,
  chatKeys,
  messagesQuery,
  threadAgentsQuery,
  topicsQuery,
} from "./chat-queries";
import { flatMessages } from "./conversation";

/** What the open conversation is about. */
export type ConversationContext = {
  /** "#social-media", or a DM's name. */
  channel: string | null;
  topic: string | null;
  /** The open thread's root message (plain text). */
  root: string | null;
  /** The DE the conversation goes to. */
  de: string | null;
};

/** `memory/search`'s `q` is at most 500 characters. */
export const MEMORY_Q_MAX = 500;
const ROOT_MAX = 300;

const squash = (s: string) => s.replace(/\s+/g, " ").trim();

/**
 * The question Recall is asked for this conversation ("" when there is nothing to ask with).
 * Every piece keeps a place within the 500 characters: the DE and the channel › topic first
 * (each bounded), the root message gets what is left (at most 300).
 */
export function memoryQuestion(c: ConversationContext): string {
  const de = squash(c.de ?? "").slice(0, 80);
  // The channel and the topic are bounded each, so a long channel name never pushes out the topic.
  const place = [squash(c.channel ?? "").slice(0, 100), squash(c.topic ?? "").slice(0, 80)]
    .filter(Boolean)
    .join(" › ");
  const fixed = [place, de].filter(Boolean);
  // ". " between each piece.
  const room = MEMORY_Q_MAX - fixed.reduce((n, p) => n + p.length + 2, 0);
  const root = c.root
    ? squash(c.root)
        .slice(0, Math.max(0, Math.min(ROOT_MAX, room)))
        .trim()
    : "";
  return [place, root, de].filter(Boolean).join(". ").slice(0, MEMORY_Q_MAX);
}

/** The conversation's short name: "#social-media › Checks". */
export function conversationLabel(c: ConversationContext): string {
  return [c.channel, c.topic].filter(Boolean).join(" › ");
}

export type ChatMemoryContext = {
  context: ConversationContext;
  /** The question for Recall; "" when the conversation gives none (the subject's default). */
  question: string;
  /** False while the conversation's pieces are still loading (do not search yet). */
  ready: boolean;
};

/**
 * The open conversation's context for the Memory tab; null outside a channel (`enabled` false, or
 * no channel in the route).
 */
export function useChatMemoryContext(
  co: string,
  focus: Focus,
  enabled: boolean,
): ChatMemoryContext | null {
  const { api } = useRouteContext({ from: "__root__" });
  const params = useParams({ strict: false }) as { channelId?: string; topicId?: string };
  const channelId = enabled ? (params.channelId ?? "") : "";
  const on = Boolean(channelId);
  const channel = useQuery({ ...channelQuery(api, co, channelId), enabled: on });
  const topics = useQuery({ ...topicsQuery(api, co, channelId), enabled: on });
  const rootId = focus.thread;
  const dm = channel.data?.kind === "dm" || channel.data?.kind === "group_dm";
  const list = topics.data?.data ?? [];
  // CHAT-TOPICS-SEPARATE: a channel with no topic in the route shows its own conversation.
  const named =
    list.find((t) => t.id === params.topicId) ??
    (params.topicId === undefined && !dm ? list.find((t) => t.is_main) : undefined);
  // The channel's stream (no topic in the route) mixes topics: the root's topic is the one whose
  // latest page holds it, as the thread panel finds it. Watched (never fetched here: the stream
  // loads them), so a page landing after the panel opened still resolves the topic.
  const streamPages = useQueries({
    queries:
      on && rootId && !named && !dm
        ? list.map((t) => ({
            queryKey: [...chatKeys.messages(co, t.thread_id), "latest"],
            queryFn: skipToken,
          }))
        : [],
  }) as UseQueryResult<{ data: { id: string }[] }>[];
  const holderAt = streamPages.findIndex((r) => r.data?.data.some((m) => m.id === rootId));
  const streamLoading = streamPages.some((r) => r.fetchStatus === "fetching");
  // Once found, a root's topic is kept while that thread stays open: newer messages may push the
  // root out of the stream's latest page, which must not drop the thread's context.
  const found = useRef<{ root: string; topic: string } | null>(null);
  if (rootId && holderAt >= 0) found.current = { root: rootId, topic: list[holderAt]?.id ?? "" };
  const kept =
    rootId && found.current?.root === rootId
      ? list.find((t) => t.id === found.current?.topic)
      : undefined;
  const topic =
    named ?? (holderAt >= 0 ? list[holderAt] : undefined) ?? kept ?? (dm ? list[0] : undefined);
  const threadId = topic?.thread_id ?? "";
  const withThread = on && Boolean(rootId) && Boolean(threadId);
  const messages = useInfiniteQuery({ ...messagesQuery(api, co, threadId), enabled: withThread });
  const rootRow = useMemo(
    () => (rootId ? flatMessages(messages.data).find((m) => m.id === rootId) : undefined),
    [messages.data, rootId],
  );
  // The addressee of the open thread, from the thread composer's query (same key). With no
  // thread open the question is the channel and topic alone.
  const agents = useQuery({
    ...threadAgentsQuery(api, co, threadId, rootId ?? undefined),
    enabled: withThread && !dm,
  });
  if (!on) return null;
  const addressee = withThread ? (agents.data?.addressee_membership_id ?? null) : null;
  const de =
    addressee && addressee !== "none"
      ? (agents.data?.data.find((r) => r.membership_id === addressee)?.name ?? null)
      : null;
  const context: ConversationContext = {
    channel: channel.data ? channelLabel(channel.data) : null,
    topic: topic && !dm && !topic.is_main ? topic.name : null,
    root: rootRow?.body_text ?? null,
    de,
  };
  const ready =
    !channel.isPending &&
    !topics.isPending &&
    // A thread whose topic is still being found waits for the stream's pages.
    !(rootId && !topic && streamLoading) &&
    (!withThread || !messages.isPending) &&
    (!withThread || dm || !agents.isPending || agents.fetchStatus === "idle");
  return { context, question: memoryQuestion(context), ready };
}
